import {FC, useEffect, useState} from "react";
import {
  useResetUserPasswordMutation, useUpdateUserMutation,
} from "api/rtkQueryApi/UsersApi";
import {
  EuiFormRow,
  EuiConfirmModal,
  EuiOverlayMask,
  EuiSelect,
  EuiLink,
  EuiText,
  EuiProgress,
  EuiCallOut, EuiFieldText,
} from "@elastic/eui";
import { User } from "api/entities/User";
import { UserRoleType } from "authentication/UserRoleType";
import {PhoneNumberToString, ShowAsPhoneNumber} from "../../utils/Formatters";

export interface IUserUpdateModal {
  onUserUpdated?: () => void;
  onClose: () => void;
  user: User;
}

export const UserUpdateModal: FC<IUserUpdateModal> = (props) => {
  const [selectedRole, setSelectedRole] = useState<UserRoleType>(
    props.user.role,
  );
  const [phoneNumber, setPhoneNumber] = useState<string>(PhoneNumberToString(props.user.phoneNumber));

  const [updateUser, updateUserRequest] = useUpdateUserMutation();
  const [resetPassword, resetPasswordRequest] = useResetUserPasswordMutation();
  const [showPasswordResetMessage, setShowPasswordResetMessage] =
    useState(false);

  const roleOptions = [
    { value: UserRoleType.Administrator, text: "Administrator" },
    { value: UserRoleType.GrowthCoach, text: "Growth Coach" },
    { value: UserRoleType.Creative, text: "Creative" },
  ];

  useEffect(() => {
    setSelectedRole(props.user.role);
    setPhoneNumber(PhoneNumberToString(props.user.phoneNumber));
  }, [props.user]);

  const onUpdateUserClick = () => {
    updateUser({ userId: props.user.id, role: selectedRole, phoneNumber: ShowAsPhoneNumber(phoneNumber) }).finally(() => {
      props.onUserUpdated?.();
    });
  };

  const onResetPasswordClick = () => {
    resetPassword({ email: props.user.email }).then(() => {
      setShowPasswordResetMessage(true);
      setTimeout(() => {
        setShowPasswordResetMessage(false);
      }, 5000);
    });
  };

  const isValidPhoneNumber = (phoneNumber: string) => {
    return /^\+?[0-9]*$/.test(phoneNumber) && phoneNumber.length === 10;
  }

  return (
    <EuiOverlayMask>
      <EuiConfirmModal
        title={"Update User"}
        onCancel={props.onClose}
        onConfirm={onUpdateUserClick}
        isLoading={updateUserRequest.isLoading}
        confirmButtonText={"Update"}
        confirmButtonDisabled={!isValidPhoneNumber(phoneNumber) || !selectedRole}
      >
        <EuiFormRow label={"User Role"}>
          <EuiSelect
            options={roleOptions}
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as UserRoleType)}
          />
        </EuiFormRow>
        <EuiFormRow label={"Phone"}>
          <EuiFieldText
              placeholder="+1234567890"
              value={phoneNumber}
              onChange={e => setPhoneNumber(e.target.value)}
              isInvalid={!isValidPhoneNumber(phoneNumber)}
          />
        </EuiFormRow>
        <EuiFormRow>
          {resetPasswordRequest.isLoading ? (
            <EuiProgress size={"xs"} />
          ) : (
            <EuiLink onClick={onResetPasswordClick}>
              <EuiText size={"s"}>Reset Password</EuiText>
            </EuiLink>
          )}
        </EuiFormRow>
        {showPasswordResetMessage && (
          <EuiFormRow>
            <EuiCallOut
              size={"s"}
              title={`Password reset email sent`}
              color="success"
              iconType="user"
            />
          </EuiFormRow>
        )}
      </EuiConfirmModal>
    </EuiOverlayMask>
  );
};
